/**
 * FERP Portal - Subgraph Registry Hook
 *
 * Provides tenant-scoped subgraph access management.
 * Integrates with SubgraphRegistry to determine which subgraphs
 * the current tenant can access.
 *
 * FERP Principles:
 * - Repository pattern: Access through GraphQL queries
 * - Data-driven: Subgraph entitlements stored in backend
 * - Life-critical: Tenant isolation for healthcare/financial data
 * - All data from backend: No mock fallbacks
 */

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';

// GraphQL Queries
// DRR-196: Use myAccessibleSubgraphs for role-aware nav filtering
// This returns the intersection of tenant-enabled AND role-allowed subgraphs
const MY_ACCESSIBLE_SUBGRAPHS = gql`
  query MyAccessibleSubgraphs($tenantId: String!) {
    myAccessibleSubgraphs(tenantId: $tenantId) {
      subgraphs
    }
  }
`;

// Legacy: tenant-only enabled subgraphs (ignores role restrictions)
const LIST_ENABLED_SUBGRAPHS = gql`
  query ListEnabledSubgraphs($tenantId: String!) {
    listEnabledSubgraphs(tenantId: $tenantId) {
      subgraphs
    }
  }
`;

const LIST_LIFE_CRITICAL_SUBGRAPHS = gql`
  query ListLifeCriticalSubgraphs($tenantId: String!) {
    listLifeCriticalSubgraphs(tenantId: $tenantId) {
      subgraphs
    }
  }
`;

const GET_SUBGRAPH_REGISTRY = gql`
  query GetSubgraphRegistry($tenantId: String!) {
    getSubgraphRegistry(tenantId: $tenantId) {
      tenantId
      registeredSubgraphs
      createdAt
      updatedAt
      version
    }
  }
`;

export interface SubgraphEntry {
  enabled: boolean;
  displayName: string;
  description: string;
  baseUrl?: string;
  s3BucketPrefix: string;
  allowedResourceTypes: string[];
  isLifeCritical: boolean;
  complianceFrameworks: string[];
  registeredAt: string;
  lastModifiedAt: string;
  registeredBy: string;
}

export interface SubgraphRegistryContext {
  /** List of enabled subgraph names for this tenant */
  enabledSubgraphs: string[];

  /** List of life-critical subgraph names */
  lifeCriticalSubgraphs: string[];

  /** Full registry with all subgraph details */
  registry: Record<string, SubgraphEntry> | null;

  /** Check if tenant has access to a specific subgraph */
  hasSubgraphAccess: (subgraphName: string) => boolean;

  /** Check if a subgraph is life-critical */
  isLifeCritical: (subgraphName: string) => boolean;

  /** Get details for a specific subgraph */
  getSubgraphDetails: (subgraphName: string) => SubgraphEntry | null;

  /** Check if tenant can use a resource type from a subgraph */
  canUseResourceType: (subgraphName: string, resourceType: string) => boolean;

  /** Loading state */
  loading: boolean;

  /** Error state */
  error: Error | null;

  /** Refresh the registry data */
  refetch: () => Promise<void>;
}

/**
 * Hook to access and manage subgraph entitlements for the current tenant.
 *
 * Usage:
 * ```tsx
 * const { hasSubgraphAccess, enabledSubgraphs } = useSubgraphRegistry(tenantId);
 *
 * // Check if tenant can use FHIR subgraph
 * if (hasSubgraphAccess('fhir')) {
 *   // Show healthcare features
 * }
 *
 * // Filter navigation by enabled subgraphs
 * const visibleItems = NAV_ITEMS.filter(item =>
 *   !item.requiredSubgraph || hasSubgraphAccess(item.requiredSubgraph)
 * );
 * ```
 */
export function useSubgraphRegistry(tenantId: string | null): SubgraphRegistryContext {
  const skipQueries = !tenantId;

  // Query accessible subgraphs (role-aware intersection)
  // DRR-196: This returns only subgraphs the user's role allows, not all tenant-enabled
  const {
    data: enabledData,
    loading: enabledLoading,
    error: enabledError,
    refetch: refetchEnabled,
  // DRR-313: Use cache-first — subgraph registry is static tenant configuration
  } = useQuery<Record<string, any>>(MY_ACCESSIBLE_SUBGRAPHS, {
    variables: { tenantId: tenantId ?? '' },
    skip: skipQueries,
    fetchPolicy: 'cache-first',
  });

  // Query life-critical subgraphs
  const {
    data: lifeCriticalData,
    loading: lifeCriticalLoading,
    error: lifeCriticalError,
    refetch: refetchLifeCritical,
  } = useQuery<Record<string, any>>(LIST_LIFE_CRITICAL_SUBGRAPHS, {
    variables: { tenantId: tenantId ?? '' },
    skip: skipQueries,
    fetchPolicy: 'cache-first',
  });

  // Query full registry for details
  const {
    data: registryData,
    loading: registryLoading,
    error: registryError,
    refetch: refetchRegistry,
  } = useQuery<Record<string, any>>(GET_SUBGRAPH_REGISTRY, {
    variables: { tenantId: tenantId ?? '' },
    skip: skipQueries,
    fetchPolicy: 'cache-first',
  });

  // Memoize enabled subgraphs list (from role-aware myAccessibleSubgraphs)
  const enabledSubgraphs = useMemo(
    () => enabledData?.myAccessibleSubgraphs?.subgraphs ?? [],
    [enabledData],
  );

  // Memoize life-critical subgraphs list
  const lifeCriticalSubgraphs = useMemo(
    () => lifeCriticalData?.listLifeCriticalSubgraphs?.subgraphs ?? [],
    [lifeCriticalData],
  );

  // Memoize registry
  const registry = useMemo(
    () => registryData?.getSubgraphRegistry?.registeredSubgraphs ?? null,
    [registryData],
  );

  // Check if tenant has access to a subgraph
  const hasSubgraphAccess = useCallback(
    (subgraphName: string): boolean => {
      return enabledSubgraphs.includes(subgraphName);
    },
    [enabledSubgraphs],
  );

  // Check if subgraph is life-critical
  const isLifeCritical = useCallback(
    (subgraphName: string): boolean => {
      return lifeCriticalSubgraphs.includes(subgraphName);
    },
    [lifeCriticalSubgraphs],
  );

  // Get subgraph details
  const getSubgraphDetails = useCallback(
    (subgraphName: string): SubgraphEntry | null => {
      if (!registry) return null;
      return registry[subgraphName] ?? null;
    },
    [registry],
  );

  // Check if tenant can use a resource type from a subgraph
  const canUseResourceType = useCallback(
    (subgraphName: string, resourceType: string): boolean => {
      if (!hasSubgraphAccess(subgraphName)) return false;

      const details = getSubgraphDetails(subgraphName);
      if (!details) return false;

      // Empty allowedResourceTypes means all types are allowed
      if (details.allowedResourceTypes.length === 0) return true;

      return details.allowedResourceTypes.includes(resourceType);
    },
    [hasSubgraphAccess, getSubgraphDetails],
  );

  // Refetch all data
  const refetch = useCallback(async () => {
    await Promise.all([refetchEnabled(), refetchLifeCritical(), refetchRegistry()]);
  }, [refetchEnabled, refetchLifeCritical, refetchRegistry]);

  const loading = enabledLoading || lifeCriticalLoading || registryLoading;
  const error = enabledError ?? lifeCriticalError ?? registryError ?? null;

  return {
    enabledSubgraphs,
    lifeCriticalSubgraphs,
    registry,
    hasSubgraphAccess,
    isLifeCritical,
    getSubgraphDetails,
    canUseResourceType,
    loading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Subgraph-aware navigation configuration type.
 * Use this to define navigation items that depend on subgraph access.
 */
export interface SubgraphNavItem {
  /** Path to navigate to */
  path: string;
  /** Display label */
  label: string;
  /** Icon component */
  icon?: React.ComponentType;
  /** Required subgraph to show this item (null = always visible) */
  requiredSubgraph?: string;
  /** Required subgraphs (all must be enabled) */
  requiredSubgraphs?: string[];
  /** Show only for life-critical subgraphs */
  lifeCriticalOnly?: boolean;
}

/**
 * Filter navigation items based on subgraph access.
 *
 * Usage:
 * ```tsx
 * const { enabledSubgraphs, lifeCriticalSubgraphs } = useSubgraphRegistry(tenantId);
 *
 * const NAV_ITEMS: SubgraphNavItem[] = [
 *   { path: '/workflows', label: 'Workflows' },
 *   { path: '/healthcare', label: 'Healthcare', requiredSubgraph: 'fhir' },
 *   { path: '/payroll', label: 'Payroll', requiredSubgraph: 'PAYROLL' },
 * ];
 *
 * const visibleItems = filterNavBySubgraph(NAV_ITEMS, enabledSubgraphs, lifeCriticalSubgraphs);
 * ```
 */
export function filterNavBySubgraph(
  items: SubgraphNavItem[],
  enabledSubgraphs: string[],
  lifeCriticalSubgraphs: string[],
): SubgraphNavItem[] {
  return items.filter((item) => {
    // No subgraph requirement - always visible
    if (!item.requiredSubgraph && !item.requiredSubgraphs) {
      // Check life-critical filter
      if (item.lifeCriticalOnly) {
        return lifeCriticalSubgraphs.length > 0;
      }
      return true;
    }

    // Single required subgraph
    if (item.requiredSubgraph) {
      return enabledSubgraphs.includes(item.requiredSubgraph);
    }

    // Multiple required subgraphs (all must be enabled)
    if (item.requiredSubgraphs) {
      return item.requiredSubgraphs.every((sg) => enabledSubgraphs.includes(sg));
    }

    return true;
  });
}

export default useSubgraphRegistry;
